import { computed, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import {
  pipe,
  tap,
  switchMap,
  throwError,
  map,
  catchError,
  forkJoin,
  of,
} from 'rxjs';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import {
  FLOW_GATEWAY,
  IWHPageableMaintenanceNotificationsDTO,
  WHHttpParams,
  WHBomNodeDataService,
  IWHBomNodeDTO,
  WHMaintenanceNotificationDOM,
  IWHShiftBookEndpointConfigModel,
  WHToastService,
} from '@workheld/workheld-shared-lib';
import { TranslateService } from '@ngx-translate/core';
import { environment } from 'src/environments/environment';
import {
  IWHUpdateMaintenanceNotificationPayload,
  MaintenanceNotificationAsyncService,
} from 'src/app/app-services-async/maintenance-notification-async.service';
import { WHNestedBomNodeDOM } from 'src/app/w-h-module/w-h-bom-node-crud-form/w-h-nested-node.model';
import { handleGlobalError } from 'src/app/app-shared/operators/global-error.operator';
import { EntryFileAsyncService } from 'src/app/app-services-async/entry-file-async.service';

interface SearchFilters extends WHHttpParams {
  startDateTime: string;
  endDateTime: string;
  notificationType: string;
  category: string;
  errorCode: string;
  notificationStatus: string;
  bomNodeId: string;
  customerId: string;
  workerId: string;
  domain: string;
  page: number;
  size: number;
  sort: string;
  filter: 'ALL' | 'UNASSIGNED' | 'ASSIGNED';
}

type MaintenanceNotificationsStore = {
  maintenanceNotifications: IWHPageableMaintenanceNotificationsDTO;
  maintenanceNotification: WHMaintenanceNotificationDOM & {
    workObjectStatusId?: number;
    workObjectDetails?: any;
    isMaintenanceNotifications?: boolean;
    customerCachedDetailURL?: string;
    workObjectCachedDetailURL?: string;
    maintenanceNotificationStatus?: string;
    managerComment?: string;
    costCenter?: string;
    contactPerson?: string;
  };
  bomNode: WHNestedBomNodeDOM;
  bomNodeName: string;
  rootBomNode: WHNestedBomNodeDOM;
  rootBomNodeName: string;
  isLoading: boolean;
  isEdit: boolean;
  filters: SearchFilters;
  searchFilterFormValues: Record<string, any> | null;
  endpointConfig: IWHShiftBookEndpointConfigModel;
};

const initialState: MaintenanceNotificationsStore = {
  maintenanceNotifications: {
    content: [],
    totalElements: 0,
    size: 0,
    number: 0,
    sort: [],
    empty: false,
    first: true,
    last: false,
    numberOfElements: 0,
    totalPages: 0,
  },
  maintenanceNotification: null,
  bomNode: null,
  bomNodeName: null,
  rootBomNode: null,
  rootBomNodeName: null,
  isLoading: false,
  isEdit: false,
  filters: {
    startDateTime: null,
    endDateTime: null,
    notificationType: null,
    category: null,
    errorCode: null,
    notificationStatus: null,
    bomNodeId: null,
    customerId: null,
    workerId: null,
    domain: null,
    page: 0,
    size: 20,
    sort: 'clientCreationDate,desc',
    filter: 'UNASSIGNED',
  },
  searchFilterFormValues: null,
  endpointConfig: { apiUrl: environment.apiUrl + FLOW_GATEWAY },
};

export const MaintenanceNotificationsStore = signalStore(
  { providedIn: 'root' },

  withState(initialState),
  withComputed(({ maintenanceNotifications }) => ({
    totalElements: computed(
      () => maintenanceNotifications()?.totalElements || 0,
    ),
    pageSize: computed(() => maintenanceNotifications()?.size || 0),
    page: computed(() => maintenanceNotifications()?.number || 0),
    activeSortHeader: computed(
      () => maintenanceNotifications()?.sort[0]?.property,
    ),
    sortDirectionHeader: computed(
      () =>
        maintenanceNotifications()?.sort[0]?.direction?.toLowerCase() || 'desc',
    ),
  })),
  withMethods(
    (
      store,
      maintenanceNotificationAsyncService = inject(
        MaintenanceNotificationAsyncService,
      ),
      ngxToastrService = inject(WHToastService),
      bomNodeDataService = inject(WHBomNodeDataService),
      translateService = inject(TranslateService),
      entryFileAsyncService = inject(EntryFileAsyncService),
    ) => {
      const loadBomNode = rxMethod<{ bomNodeId: string }>(
        pipe(
          switchMap(({ bomNodeId }) =>
            bomNodeDataService
              .getBomNodeDTO({
                apiUrl: environment.apiUrl + FLOW_GATEWAY,
                bomNodeId: bomNodeId,
              })
              .pipe(
                switchMap((bomNode: IWHBomNodeDTO) => {
                  const rootNodeId = (bomNode as any)?.rootNodeId as
                    string | undefined;
                  const loadRootNode$ = rootNodeId
                    ? bomNodeDataService
                        .getBomNodeDTO({
                          apiUrl: environment.apiUrl + FLOW_GATEWAY,
                          bomNodeId: rootNodeId,
                        })
                        .pipe(catchError(() => of(null)))
                    : of(null);

                  return forkJoin({
                    bomNode: of(bomNode),
                    rootBomNode: loadRootNode$,
                  });
                }),
                map(({ bomNode, rootBomNode }) => {
                  patchState(store, {
                    bomNode: new WHNestedBomNodeDOM(bomNode),
                    bomNodeName: bomNode?.name,
                    rootBomNode: rootBomNode
                      ? new WHNestedBomNodeDOM(rootBomNode)
                      : null,
                    rootBomNodeName: rootBomNode?.name || null,
                  });
                }),
                handleGlobalError(),
              ),
          ),
        ),
      );
      return {
        setPage(page: number): void {
          patchState(store, (state) => ({
            filters: { ...state.filters, page },
          }));
        },
        setSearchTerm(term: string): void {
          patchState(store, (state) => ({
            filters: { ...state.filters, term },
          }));
        },
        setSortMode(sort: string): void {
          patchState(store, (state) => ({
            filters: { ...state.filters, sort },
          }));
        },
        setSearchFilters(filters: Partial<SearchFilters>): void {
          patchState(store, (state) => ({
            filters: { ...state.filters, ...filters, page: 0 },
          }));
        },
        setSearchFilterFormValues(values: Record<string, any>): void {
          patchState(store, { searchFilterFormValues: values });
        },
        setIsEditMode(isEdit: boolean): void {
          patchState(store, (state) => ({
            isEdit,
          }));
        },
        setMaintenanceNotification(
          maintenanceNotification: WHMaintenanceNotificationDOM,
        ): void {
          patchState(store, { maintenanceNotification });
        },
        clearSelectedMaintenanceNotification(): void {
          patchState(store, {
            maintenanceNotification: null,
            bomNode: null,
            bomNodeName: null,
            rootBomNode: null,
            rootBomNodeName: null,
          });
        },

        loadMaintenanceNotifications: rxMethod<{ params: SearchFilters }>(
          pipe(
            tap(() => patchState(store, { isLoading: true })),
            switchMap(({ params }) =>
              maintenanceNotificationAsyncService
                .getMaintenanceNotifications(params)
                .pipe(
                  map((data) => {
                    patchState(store, {
                      maintenanceNotifications: data,
                      isLoading: false,
                    });
                  }),
                  handleGlobalError({
                    onError: () => patchState(store, { isLoading: false }),
                  }),
                ),
            ),
          ),
        ),

        loadMaintenanceNotificationDetails: rxMethod<{
          maintenanceNotificationId: string;
        }>(
          pipe(
            tap(() => patchState(store, { isLoading: true })),
            switchMap(({ maintenanceNotificationId }) =>
              maintenanceNotificationAsyncService
                .getMaintenanceNotification(maintenanceNotificationId)
                .pipe(
                  map((maintenanceNotification) => {
                    patchState(store, {
                      maintenanceNotification: {
                        ...store.maintenanceNotification(),
                        ...maintenanceNotification,
                      },
                      isLoading: false,
                    });
                    if (maintenanceNotification?.bomNodeId) {
                      loadBomNode({
                        bomNodeId: maintenanceNotification.bomNodeId,
                      });
                    }
                  }),
                  handleGlobalError({
                    onError: () => patchState(store, { isLoading: false }),
                  }),
                ),
            ),
          ),
        ),
        loadBomNode,
        dispatchMaintenanceNotification: rxMethod<{
          maintenanceNotificationId: string;
          onSuccess?: () => void;
        }>(
          pipe(
            tap(() => patchState(store, { isLoading: true })),
            switchMap(({ maintenanceNotificationId, onSuccess }) =>
              maintenanceNotificationAsyncService
                .dispatchMaintenanceNotification(maintenanceNotificationId)
                .pipe(
                  tap((response) => {
                    patchState(store, { isLoading: false });
                    if (response?.extId) {
                      ngxToastrService.success(
                        translateService.instant(
                          'maintenanceplan.ui.successgenerate.notification',
                          { extId: response.extId },
                        ),
                      );
                    } else {
                      ngxToastrService.success(
                        translateService.instant(
                          'maintenancenotification.sendToSap.notification',
                        ),
                      );
                    }
                    if (onSuccess) {
                      onSuccess();
                    }
                  }),
                  handleGlobalError({
                    onError: () => patchState(store, { isLoading: false }),
                  }),
                ),
            ),
          ),
        ),
        updateMaintenanceNotification: rxMethod<{
          maintenanceNotificationId: string;
          payload: IWHUpdateMaintenanceNotificationPayload;
          onSuccess?: () => void;
        }>(
          pipe(
            tap(() => patchState(store, { isLoading: true })),
            switchMap(({ maintenanceNotificationId, payload, onSuccess }) => {
              return maintenanceNotificationAsyncService
                .manualUpdateMaintenanceNotification(
                  maintenanceNotificationId,
                  payload,
                )
                .pipe(
                  tap(() => {
                    patchState(store, { isLoading: false });
                    ngxToastrService.success(
                      'entry.ui.updatesuccess.notification',
                    );
                    if (onSuccess) {
                      onSuccess();
                    }
                  }),
                  handleGlobalError({
                    onError: () => patchState(store, { isLoading: false }),
                  }),
                );
            }),
          ),
        ),
        /**
         * Persists the staged edits of an existing MN (WHP-12151): text fields
         * first, then the staged picture deletions and uploads. The reloaded MN
         * is patched back into the store so the detail view refreshes in place.
         */
        saveMaintenanceNotificationEdit: rxMethod<{
          maintenanceNotificationId: string;
          payload?: IWHUpdateMaintenanceNotificationPayload | null;
          filesToUpload?: File[];
          fileIdsToDelete?: string[];
          onSuccess?: () => void;
        }>(
          pipe(
            tap(() => patchState(store, { isLoading: true })),
            switchMap(
              ({
                maintenanceNotificationId,
                payload,
                filesToUpload,
                fileIdsToDelete,
                onSuccess,
              }) => {
                const textUpdate$ =
                  payload && Object.keys(payload).length > 0
                    ? maintenanceNotificationAsyncService.manualUpdateMaintenanceNotification(
                        maintenanceNotificationId,
                        payload,
                      )
                    : of(null);

                const fileDeletions$ = (fileIdsToDelete || []).map((fileId) =>
                  maintenanceNotificationAsyncService.deleteMaintenanceNotificationFile(
                    fileId,
                  ),
                );
                const fileUploads$ = (filesToUpload || []).map((file) =>
                  maintenanceNotificationAsyncService.uploadMaintenanceNotificationFile(
                    maintenanceNotificationId,
                    file,
                  ),
                );

                return textUpdate$.pipe(
                  switchMap(() =>
                    fileDeletions$.length > 0
                      ? forkJoin(fileDeletions$)
                      : of(null),
                  ),
                  switchMap(() =>
                    fileUploads$.length > 0 ? forkJoin(fileUploads$) : of(null),
                  ),
                  switchMap(() =>
                    maintenanceNotificationAsyncService.getMaintenanceNotification(
                      maintenanceNotificationId,
                    ),
                  ),
                  tap((maintenanceNotification) => {
                    patchState(store, {
                      maintenanceNotification: {
                        ...store.maintenanceNotification(),
                        ...maintenanceNotification,
                      },
                      isLoading: false,
                    });
                    ngxToastrService.success(
                      'entry.ui.updatesuccess.notification',
                    );
                    if (onSuccess) {
                      onSuccess();
                    }
                  }),
                  handleGlobalError({
                    onError: () => patchState(store, { isLoading: false }),
                  }),
                );
              },
            ),
          ),
        ),
        addMaintenanceNotification: rxMethod<{
          payload;
          files;
          onSuccess?: (createdEntry: WHMaintenanceNotificationDOM) => void;
        }>(
          pipe(
            tap(() => patchState(store, { isLoading: true })),
            switchMap(({ payload, files, onSuccess }) =>
              maintenanceNotificationAsyncService
                .createMaintenanceNotification(payload)
                .pipe(
                  switchMap((createdEntry: WHMaintenanceNotificationDOM) => {
                    if (files && files.length > 0) {
                      // Upload each file through the unified single-endpoint API.
                      const fileUploads$ = files.map((file: File) => {
                        return entryFileAsyncService.uploadEntryFile(
                          createdEntry.id,
                          file,
                        );
                      });

                      return forkJoin(fileUploads$).pipe(
                        map(() => createdEntry),
                        catchError((error) => {
                          patchState(store, { isLoading: false });
                          return throwError(error);
                        }),
                      );
                    }
                    return of(createdEntry);
                  }),
                  tap((createdEntry: WHMaintenanceNotificationDOM) => {
                    patchState(store, { isLoading: false });
                    ngxToastrService.success(
                      'entry.ui.createsuccess.notification',
                    );

                    if (onSuccess) {
                      onSuccess(createdEntry);
                    }
                  }),
                  handleGlobalError({
                    onError: () => patchState(store, { isLoading: false }),
                  }),
                ),
            ),
          ),
        ),
      };
    },
  ),
);
